// admin/src/features/exercises/HowToEditor.tsx
//
// Wave 16d: edits what the app's exercise screen shows under "How to",
// "Tips" and "Avoid", plus the focus line -- English and Hebrew side by
// side, one item per line. Migration 0045 seeded every library exercise;
// clearing a list makes the app fall back to its bundled text.
import { useEffect, useState } from 'react';

import { Button } from '../../components/Button';
import { TextAreaField, TextField } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { friendlyErrorMessage } from '../../lib/errors';
import {
  HOW_TO_LISTS,
  linesToList,
  listToLines,
  validateFocus,
  validateHowToList,
  type HowToListKey,
  type HowToRow,
} from '../../lib/howTo';
import { getExerciseHowTo, updateExerciseHowTo } from './api';

type Draft = Record<`${HowToListKey}_${'en' | 'he'}`, string> & { focus_en: string; focus_he: string };

const EMPTY: Draft = {
  description_steps_en: '',
  description_steps_he: '',
  tips_en: '',
  tips_he: '',
  mistakes_en: '',
  mistakes_he: '',
  focus_en: '',
  focus_he: '',
};

function toDraft(row: HowToRow | null): Draft {
  if (!row) return EMPTY;
  return {
    description_steps_en: listToLines(row.description_steps_en),
    description_steps_he: listToLines(row.description_steps_he),
    tips_en: listToLines(row.tips_en),
    tips_he: listToLines(row.tips_he),
    mistakes_en: listToLines(row.mistakes_en),
    mistakes_he: listToLines(row.mistakes_he),
    focus_en: row.focus_en ?? '',
    focus_he: row.focus_he ?? '',
  };
}

function toRow(draft: Draft): HowToRow {
  return {
    description_steps_en: linesToList(draft.description_steps_en),
    description_steps_he: linesToList(draft.description_steps_he),
    tips_en: linesToList(draft.tips_en),
    tips_he: linesToList(draft.tips_he),
    mistakes_en: linesToList(draft.mistakes_en),
    mistakes_he: linesToList(draft.mistakes_he),
    focus_en: draft.focus_en.trim() || null,
    focus_he: draft.focus_he.trim() || null,
  };
}

export function HowToEditor({ exerciseId }: { exerciseId: string }) {
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string | null>>>({});

  useEffect(() => {
    let alive = true;
    getExerciseHowTo(exerciseId)
      .then((row) => {
        if (!alive) return;
        setDraft(toDraft(row));
        setState('ready');
      })
      .catch(() => {
        // The columns arrive with migration 0045; until then there is nothing to edit.
        if (alive) setState('unavailable');
      });
    return () => {
      alive = false;
    };
  }, [exerciseId]);

  const set = (key: keyof Draft) => (value: string) => setDraft((prev) => ({ ...prev, [key]: value }));

  async function save() {
    const next: Partial<Record<keyof Draft, string | null>> = {
      focus_en: validateFocus(draft.focus_en),
      focus_he: validateFocus(draft.focus_he),
    };
    for (const { key } of HOW_TO_LISTS) {
      next[`${key}_en`] = validateHowToList(draft[`${key}_en`]);
      next[`${key}_he`] = validateHowToList(draft[`${key}_he`]);
    }
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    setSaving(true);
    try {
      await updateExerciseHowTo(exerciseId, toRow(draft));
      toast.show('How-to saved', 'success');
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 760, marginBottom: 20 }} data-testid="howto-editor">
      <div className="topbar" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 22 }}>How to</h2>
      </div>
      {state === 'loading' ? <Spinner /> : null}
      {state === 'unavailable' ? <p className="muted">Available once the database update (migration 0045) is live.</p> : null}
      {state === 'ready' ? (
        <>
          <div className="row">
            <TextField label="Focus (English)" value={draft.focus_en} onChange={(e) => set('focus_en')(e.target.value)} error={errors.focus_en} hint="What it trains or fixes, a few words." />
            <TextField label="Focus (Hebrew)" dirAuto value={draft.focus_he} onChange={(e) => set('focus_he')(e.target.value)} error={errors.focus_he} />
          </div>
          {HOW_TO_LISTS.map(({ key, label, hint }) => (
            <div className="row" key={key}>
              <TextAreaField
                label={`${label} (English)`}
                rows={5}
                value={draft[`${key}_en`]}
                onChange={(e) => set(`${key}_en`)(e.target.value)}
                error={errors[`${key}_en`]}
                hint={hint}
              />
              <TextAreaField
                label={`${label} (Hebrew)`}
                rows={5}
                dirAuto
                value={draft[`${key}_he`]}
                onChange={(e) => set(`${key}_he`)(e.target.value)}
                error={errors[`${key}_he`]}
              />
            </div>
          ))}
          <Button onClick={save} disabled={saving} style={{ width: '100%' }}>
            {saving ? 'Saving…' : 'Save how-to'}
          </Button>
        </>
      ) : null}
    </div>
  );
}
